import { ActionButton } from '@knocklabs/client';
import { Box, Divider, List } from '@mui/material';
import { Fragment } from 'react';
import { NOTIFICATIONS_FEED_TOUR_LIST_ID } from '../../tour/useNotificationsTour';
import { NotificationFeedListItemModel } from '../item/map-notification-feed-item';
import { NotificationFeedItemView } from '../item/NotificationFeedItemView';
import { NotificationFeedFilter } from './notification-feed-filter';
import { NotificationFeedEmptyState } from './NotificationFeedEmptyState';
import { NotificationFeedHeader } from './NotificationFeedHeader';
import { NotificationFeedListSkeleton } from './NotificationFeedListSkeleton';
import { NotificationFeedLoadMore } from './NotificationFeedLoadMore';

export type NotificationFeedListItem = NotificationFeedListItemModel;

export type NotificationFeedListProps = {
  items: NotificationFeedListItem[];
  unreadCount: number;
  filter: NotificationFeedFilter;
  onFilterChange: (next: NotificationFeedFilter) => void;
  onItemClick: (id: string) => void;
  onArchive: (id: string) => void;
  onButtonClick: (id: string, button: ActionButton) => void;
  onMarkAllAsRead: () => void;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
};

/**
 * Presentational MUI notifications list (header, empty state, items, load more).
 * Dividers span the full list width between items.
 */
export function NotificationFeedList({
  items,
  unreadCount,
  filter,
  onFilterChange,
  onItemClick,
  onArchive,
  onButtonClick,
  onMarkAllAsRead,
  hasMore,
  isLoading,
  isLoadingMore,
  onLoadMore,
}: NotificationFeedListProps) {
  return (
    <Box
      id={NOTIFICATIONS_FEED_TOUR_LIST_ID}
      sx={{ width: '100%', maxHeight: 420, display: 'flex', flexDirection: 'column' }}
    >
      <NotificationFeedHeader
        unreadCount={unreadCount}
        filter={filter}
        onFilterChange={onFilterChange}
        onMarkAllAsRead={onMarkAllAsRead}
      />
      {isLoading && items.length === 0 ? (
        <NotificationFeedListSkeleton />
      ) : items.length === 0 ? (
        <NotificationFeedEmptyState />
      ) : (
        <List disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
          {items.map((item) => (
            <Fragment key={item.id}>
              <NotificationFeedItemView
                eyebrow={item.eyebrow}
                title={item.title}
                bodyHtml={item.bodyHtml}
                timeLabel={item.timeLabel}
                unread={item.unread}
                avatarName={item.avatarName}
                avatarSrc={item.avatarSrc}
                buttons={item.buttons}
                hasAction={item.hasAction}
                severity={item.severity}
                onClick={item.hasAction ? () => onItemClick(item.id) : undefined}
                onArchive={() => onArchive(item.id)}
                onButtonClick={(button) => onButtonClick(item.id, button)}
              />
              <Divider sx={{ borderColor: 'divider', opacity: 0.5 }} />
            </Fragment>
          ))}
          <NotificationFeedLoadMore
            hasMore={hasMore}
            loading={isLoadingMore}
            onLoadMore={onLoadMore}
          />
        </List>
      )}
    </Box>
  );
}
